'use client';

import { yupResolver } from '@hookform/resolvers/yup';
import { Button, ControlledInput, Typography } from 'components';
import PasswordValidator from 'components/PasswordValidator';
import { PRIVACY_POLICY_LINK, TERMS_CONDITIONS_LINK } from 'constants/links';
import { Maybe } from 'gql/graphql';
import { handlerEnter } from 'handlers/handlerEnter';
import { onErrorHelper } from 'handlers/handlerMutations';
import { toaster } from 'helpers/toaster';
import useAuth from 'hooks/useAuth';
import { useTranslations } from 'next-intl';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useAccountPassword } from 'providers/AuthProviders/useAccountPassword';
import { useClientInvite } from 'providers/AuthProviders/useClientInvite';
import { useCallback, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { notifyException } from 'services/appsignal';
import { KeysEnum } from 'types/KeysEnum';
import { TGenericError } from 'types/TGenericError';
import { getQueryParam } from 'utils/paramsUtil';
import * as yup from 'yup';
import YupPassword from 'yup-password';

YupPassword(yup);

export default function AcceptInvitation() {
  const auth = useAuth();
  const router = useRouter();
  const t = useTranslations();

  const token = getQueryParam(KeysEnum.Token) as string;
  const redirectTo = getQueryParam(KeysEnum.RedirectTo) as string;

  const { data: clientInvite, isFetched } = useClientInvite(
    {
      token
    },
    { enabled: !!token }
  );

  const schema = yup.object().shape({
    firstName: yup.string().required(t('messages.fields.first_name_required')),
    lastName: yup.string().required(t('messages.fields.last_name_required')),
    companyName: yup.string().optional(),
    password: yup.string().password().required(t('messages.fields.password_required')),
    passwordConfirmation: yup
      .string()
      .required(t('messages.fields.confirm_password_required'))
      .test(
        'passwordConfirmation',
        t('messages.fields.password_must_match'),
        function (value: string | undefined) {
          return this.parent.password === value;
        }
      ),
    termsAccepted: yup
      .boolean()
      .is([true], t('messages.fields.accept_terms'))
      .required(t('messages.fields.accept_terms_required'))
  });

  type MemberInfo = {
    firstName?: Maybe<string>;
    lastName?: Maybe<string>;
    companyName?: Maybe<string>;
    password: string;
    passwordConfirmation: string;
    termsAccepted: boolean;
  };

  const {
    control,
    handleSubmit,
    watch,
    formState: { errors },
    getValues,
    reset
  } = useForm<MemberInfo>({
    resolver: yupResolver(schema)
  });

  useEffect(() => {
    if (isFetched) {
      reset({
        firstName: clientInvite?.user?.firstName,
        lastName: clientInvite?.user?.lastName,
        companyName: clientInvite?.clientAccount?.name
      });
    }
  }, [isFetched, clientInvite, reset]);

  const firstName = watch('firstName');
  const lastName = watch('lastName');
  const password = watch('password');

  const { mutateAsync: accountPassword } = useAccountPassword({
    onSuccess: () => toaster.success(t('messages.success.accept_invite')),
    onError: onErrorHelper as (data: TGenericError) => void
  });

  const onFetch = useCallback(async () => {
    try {
      const data = getValues();
      await accountPassword({
        password: data.password,
        passwordConfirmation: data.passwordConfirmation,
        firstName: data.firstName,
        lastName: data.lastName,
        companyName: data.companyName,
        token
      });
      if (redirectTo) {
        await auth.signIn(clientInvite?.user?.email as string, data.password);

        return router.push(redirectTo);
      }
      router.push('/auth/sign-in');
    } catch (error) {
      notifyException(error);
    }
  }, [getValues, accountPassword, router, token, redirectTo, clientInvite]);

  const onSubmit = handleSubmit(onFetch);

  return (
    <>
      <div className="flex flex-col">
        <Typography variant="medium7xl" className={'text-tertiary-100'}>
          {t('auth.client_accept_invite.title')}
        </Typography>
        <div className="pt-2">
          <div>
            <Typography variant="light4" className="text-tertiary-200">
              {t('auth.client_accept_invite.subtitle')}
            </Typography>
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-4 px-1 pt-8">
        <div className="flex w-full gap-4">
          <ControlledInput
            control={control}
            disabled={!!clientInvite?.user?.firstName}
            error={errors.firstName}
            label={t('auth.sign_up.label_first_name')}
            type="text"
            name="firstName"
            classNameParent="w-full"
            placeholder={t('auth.sign_up.placeholder_first_name')}
          />
          <ControlledInput
            control={control}
            error={errors.lastName}
            disabled={!!clientInvite?.user?.lastName}
            label={t('auth.sign_up.label_last_name')}
            type="text"
            name="lastName"
            classNameParent="w-full"
            placeholder={t('auth.sign_up.placeholder_last_name')}
          />
        </div>
        <ControlledInput
          control={control}
          disabled={!!clientInvite?.clientAccount?.name}
          error={errors.companyName}
          label={t('auth.sign_up.label_company_name')}
          type="text"
          name="companyName"
          autoComplete="off"
          role="presentation"
          classNameParent="w-full"
          placeholder={t('auth.sign_up.placeholder_company_name')}
        />
        <div>
          <ControlledInput
            control={control}
            error={errors.password}
            label={t('auth.sign_up.label_password')}
            type="password"
            name="password"
            autoComplete="new-password"
            role="presentation"
            classNameParent="w-full"
            placeholder={t('auth.sign_up.placeholder_password')}
          />
          <PasswordValidator password={password} />
        </div>
        <ControlledInput
          control={control}
          error={errors.passwordConfirmation}
          label={t('auth.sign_up.label_confirm_password')}
          type="password"
          name="passwordConfirmation"
          classNameParent="w-full"
          placeholder={t('auth.sign_up.placeholder_confirm_password')}
        />
        <ControlledInput
          control={control}
          error={errors.termsAccepted}
          type="checkbox"
          name="termsAccepted"
          label={
            <Typography variant="regular5">
              {t('auth.sign_up.agree_terms')}
              <Link
                href={TERMS_CONDITIONS_LINK}
                target="_blank"
                className="mx-1 cursor-pointer text-secondary-link"
              >
                {t('auth.sign_up.terms_conditions')}
              </Link>
              {t('auth.sign_up.and')}
              <Link
                href={PRIVACY_POLICY_LINK}
                target="_blank"
                className="ml-1 cursor-pointer text-secondary-link"
              >
                {t('auth.sign_up.privacy_policy')}
              </Link>
              .
            </Typography>
          }
        />
        <div className="w-full">
          <Button
            onClick={onSubmit}
            onKeyDown={handlerEnter(onSubmit)}
            label={t('auth.client_accept_invite.complete_registration')}
            className="w-full"
          />
        </div>
      </div>
    </>
  );
}
